import { cumsum, round } from 'mathjs';
import Plotly from "plotly.js-basic-dist-min";
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import createPlotlyComponent from "react-plotly.js/factory";
import { Col, Row } from 'reactstrap';
import { useFetchWrapper } from '../../../hooks/useFetchWrapper';
import { clientConfig } from '../../../utilities/configService';

//import { number } from 'prop-types';
const Plot = createPlotlyComponent(Plotly);

interface ISusImpactProps {
    fleetID: string,
    staticData?: string,
    textCMSContent?: string,
}

export const SustainedImpact = ({ fleetID, staticData, textCMSContent }: ISusImpactProps) => {
    const fetchWrapper = useFetchWrapper();
    const applicationName = clientConfig?.APPLICATION_NAME || '';

    interface IMeasure {
        year: number;
        totalAnnualEnergyCost: number;
    }

    interface IChartModel {
        layoutCosts: any;
        layoutEmissions: any;
        dataCosts: [{}, {}];
        dataEmissions: [{}, {}];
        config: {}
    }

    const [costYears, setCostYears] = useState(15);
    const [emissionYears, setEmissionYears] = useState(15);
    const [costSavings, setCostSavings] = useState(0);
    const [emissionSavings, setEmissionSavings] = useState(0);
    const [hasData, setHasData] = useState(false);
    const [data, setData] = useState<any>({});
    const [chartModel, setChartModel] = useState<IChartModel | any>({});
    const [useStaticData, setUseStaticData] = useState(staticData && staticData !== '');
    const [showEmissionsDecimals, setShowEmissionsDecimals] = useState(false);
    //const createCharts = useCallback((data: any) => {
    const createCharts = useCallback((data: any) => {
        //extract x values for years
        let xValuesCost = data.existingAnnualCostImpact.map((a: IMeasure) => a.year).slice(0, costYears + 1);
        let xValuesEmissions = data.existingAnnualCostImpact.map((a: IMeasure) => a.year).slice(0, emissionYears + 1);
        //transform costs to cumulative sums
        let yValuesExistingCost = useStaticData ? data.existingAnnualCostImpact.map((a: IMeasure) => a.totalAnnualEnergyCost).slice(0, costYears + 1)
            : cumsum(data.existingAnnualCostImpact.map((a: IMeasure) => Math.round(a.totalAnnualEnergyCost)).slice(0, costYears + 1));
        let yValuesReplacementCost = useStaticData ? data.replacementAnnualCostImpact.map((a: IMeasure) => a.totalAnnualEnergyCost).slice(0, costYears + 1)
            : cumsum(data.replacementAnnualCostImpact.map((a: IMeasure) => Math.round(a.totalAnnualEnergyCost)).slice(0, costYears + 1));
        let yValuesExistingEmissions = useStaticData ? []
            : cumsum(data.existingAnnualEmissionsImpact.map((a: IMeasure) => a.totalAnnualEnergyCost).slice(0, emissionYears + 1));
        let yValuesReplacementEmissions = useStaticData ? []
            : cumsum(data.replacementAnnualEmissionsImpact.map((a: IMeasure) => a.totalAnnualEnergyCost).slice(0, emissionYears + 1));

        //set savings values
        let existing: number = parseInt(yValuesExistingCost[costYears].toString());
        let replacement: number = parseInt(yValuesReplacementCost[costYears].toString());
        setCostSavings(existing - replacement);
        let yAxisLabel = 'Cumulative fuel, operations<br>and maintenance costs';

        if (!useStaticData) {
            existing = parseFloat(yValuesExistingEmissions[emissionYears].toString());
            replacement = parseFloat(yValuesReplacementEmissions[emissionYears].toString());
            var savings = existing - replacement;
            var localShowEmissionsDecimals = false;
            if (savings < 100) {
                setShowEmissionsDecimals(true);
                localShowEmissionsDecimals = true;

            }
            setEmissionSavings(localShowEmissionsDecimals ? round(savings, 1) : round(savings));
        }

        //setup chart trace
        var traceExisingCost = {
            x: xValuesCost,
            y: yValuesExistingCost,
            xaxis: 'x',
            yaxis: 'y',
            name: '<b>Fossil Fuel Vehicle Costs</b>',
            mode: 'lines',
            marker: { color: '#8B8782' },
            line: { width: 3 }
        };
        var traceReplacementCost = {
            x: xValuesCost,
            y: yValuesReplacementCost,
            xaxis: 'x',
            yaxis: 'y',
            name: '<b>Electric Vehicle Costs</b>',
            mode: 'lines',
            marker: { color: '#0070D0' },
            line: { width: 3 }
        };
        var traceExisingEmissions = {
            x: xValuesEmissions,
            y: yValuesExistingEmissions,
            xaxis: 'x',
            yaxis: 'y',
            name: '<b>Fossil Fuel Vehicle Emissions</b>',
            mode: 'lines',
            marker: { color: '#8B8782' },
            line: { width: 3 },
            //hovertemplate: "<b>Year %{x}</b><br>%{fullData.name}: %{y:.2f}<extra></extra>"
        };
        var traceReplacementEmissions = {
            x: xValuesEmissions,
            y: yValuesReplacementEmissions,
            xaxis: 'x',
            yaxis: 'y',
            name: '<b>Electric Vehicle Emissions</b>',
            mode: 'lines',
            marker: { color: '#2E944A' },
            line: { width: 3 },
            //hovertemplate: "%{fullData.name}: %{y:.2f}%{_xother}<extra></extra>"
        };

        var baseLegend = {
            x: [null],
            y: [null],
            visible: 'legendonly',
            type: 'bar',
            name: '<b>Fossil Fuel Vehicle Emissions</b>',
            marker: { color: '#8B8782' }
        };

        var layoutCost = {

            font: {
                family: getComputedStyle(document.documentElement).getPropertyValue('--Font-Family'),
            },
            showlegend: true,
            legend: {
                "orientation": "h",
                "xanchor": "center",
                "x": "0.5",
                "y": "1.18",
                font: {
                    size: 13,
                    color: '#000'
                },
                "itemclick": false,
                "itemdoubleclick": false
            },
            //autosize: true,
            //width: 780,
            height: 275,
            margin: {
                l: 75, r: 10, t: 0, pad: 5, b: 45, autoexpand: true
            },
            xaxis: {
                title: {
                    text: 'Years',
                    font: {
                        size: 12,
                        color: '#000'
                    },
                    standoff: 15
                },
                gridcolor: '#999',
                gridwidth: 1,
                //linecolor: '#999',
                //linewidth: 1,
                tickfont: {
                    size: 12,
                    color: '#000'
                },
                showgrid: false,
                fixedrange: true,
                dtick: 1,
                //    range: [-0.5, xValues.length-0.5],
                zeroline: false,
            },
            yaxis: {
                title: {
                    text: yAxisLabel,
                    standoff: 15,
                    font: {
                        size: 12,
                        color: '#000'
                    },
                },
                tickfont: {
                    size: 12,
                    color: '#000'
                },
                gridcolor: '#999',
                gridwidth: 1,
                //linecolor: '#999',
                //linewidth: 1,
                zeroline: false,
                //tickformatstops: [{
                //    "dtickrange": [null, costSavings],
                //        "value": ".0s"
                //    }]

            },
            hovermode: 'x unified'
        };
        //var layoutEmissions = Object.assign({}, layoutCost);
        let layoutEmissions = structuredClone(layoutCost);
        layoutEmissions.yaxis.title.text = 'Metric tons of CO2';

        var chartConfig = {
            displayModeBar: false
        };

        var newChartModel: IChartModel = {
            layoutCosts: layoutCost,
            layoutEmissions: layoutEmissions,
            //dataCosts: [traceExisingCost, traceReplacementCost, baseLegend],
            dataCosts: [traceExisingCost, traceReplacementCost],
            dataEmissions: [traceExisingEmissions, traceReplacementEmissions],
            config: chartConfig
        };

        setChartModel(newChartModel);

    }, [costYears, emissionYears]);

    const getSustainedImpact = useCallback(() => {
        if (useStaticData) {
            setHasData(true);
            setData(staticData);
            createCharts(staticData);
        }
        else {
            fetchWrapper.get(`FleetEstimator/SustainedImpact/${applicationName}/${fleetID}`)
                .then((data) => {
                    if (data.success) {
                        setHasData(true);
                        setData(data);
                        //create chart data from 
                        createCharts(data);
                    }
                    else {
                        setHasData(false);
                    }
                });
        }
    }, [createCharts]);

    useEffect(() => {
        getSustainedImpact();
    }, [getSustainedImpact]);

    const handleCostChange = (e: ChangeEvent<HTMLInputElement>) => {
        setCostYears(parseInt(e.target.value));
        createCharts(data);
    }
    const handleEmissionChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (!useStaticData) {
            setEmissionYears(parseInt(e.target.value));
            createCharts(data);
        }
    }

    const handleOnHoverCosts = (_: any) => {
        _.points.forEach((point: any) => {
            var update = {
                'hovertemplate': point.data.name + ': $' + point.y.toLocaleString() + '<extra></extra>'
            };
            Plotly.restyle('costChart', update, point.curveNumber);
        });
    }
    const handleOnHoverEmissions = (_: any) => {
        var decimalPlaces = showEmissionsDecimals ? 2 : 1;

        _.points.forEach((point: any) => {
            var update = {
                'hovertemplate': point.data.name + ': ' + round(point.y, decimalPlaces).toLocaleString(undefined, { minimumFractionDigits: decimalPlaces }) + '<extra></extra>'
            };
            Plotly.restyle('emissionsChart', update, point.curveNumber);
        });
    }

    return (
        <Row>
            <Col>
                {/* cost saving */}
                <Row className="accentThreeBorder vehicleTable">
                    <Col sm={12} className="accentThreeBackground siChartTitle">
                        <Row>
                            <Col lg={6} className="siChartTitleText">{useStaticData && "Sample "}Comparison of Operational Costs Over Time</Col>
                            <Col lg={6} className={'siChartFilter'}>
                                <Row>
                                    <Col className="radio">
                                        <label>
                                            <input type="radio" value="5" className="radio-button-color" checked={costYears === 5} onChange={handleCostChange} />&nbsp;
                                            5 years
                                        </label>
                                    </Col>
                                    <Col className="radio">
                                        <label>
                                            <input type="radio" value="10" className="radio-button-color" checked={costYears === 10} onChange={handleCostChange} />&nbsp;
                                            10 years
                                        </label>
                                    </Col>
                                    <Col className="radio">
                                        <label>
                                            <input type="radio" value="15" className="radio-button-color" checked={costYears === 15} onChange={handleCostChange} />&nbsp;
                                            15 years
                                        </label>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </Col>
                    {!useStaticData &&
                        <Col sm={12} className="siChartConclusion" style={{ color: "var(--Accent-3)" }}>
                            Conclusion: You will save ${costSavings.toLocaleString()} over {costYears} years
                        </Col>
                    }
                    <Col sm={12} className="siChartBox" >
                        {(hasData !== undefined && hasData) &&
                            <Plot divId="costChart" className='costChart' key='costChart' data={chartModel.dataCosts} layout={chartModel.layoutCosts} config={chartModel.config} useResizeHandler={true} style={{ width: "100%" }} onHover={handleOnHoverCosts} />
                        }
                        {(hasData === undefined || !hasData) &&
                            <div className="heading5">No Data Available</div>
                        }
                    </Col>
                    {/* only display CMS subtext content on sample cost savings table shown on UI homepage. */}
                    {useStaticData && textCMSContent &&
                        <Col className={'siChartBoxTextContent'}>
                            {textCMSContent}
                        </Col>
                    }
                </Row>
                {/* emissions saving */}
                {!useStaticData &&
                    <Row className="batteryTwoBorder">
                        <Col sm={12} className="batteryTwoBackground siChartTitle">
                            <Row>
                                <Col lg={6} className="siChartTitleText">Comparison of GHG Emissions Over Time</Col>
                                <Col lg={6} className={'siChartFilter'}>
                                    <Row>
                                        <Col className="radio">
                                            <label>
                                                <input type="radio" value="5" className="radio-button-color" checked={emissionYears === 5} onChange={handleEmissionChange} />&nbsp;
                                                5 years
                                            </label>
                                        </Col>
                                        <Col className="radio">
                                            <label>
                                                <input type="radio" value="10" className="radio-button-color" checked={emissionYears === 10} onChange={handleEmissionChange} />&nbsp;
                                                10 years
                                            </label>
                                        </Col>
                                        <Col className="radio">
                                            <label>
                                                <input type="radio" value="15" className="radio-button-color" checked={emissionYears === 15} onChange={handleEmissionChange} />&nbsp;
                                                15 years
                                            </label>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        </Col>
                        <Col sm={12} className="siChartConclusion" style={{ color: "var(--Battery-2)" }}>
                            Conclusion: You will reduce your GHG emissions by {emissionSavings.toLocaleString()} tons over {emissionYears} years
                        </Col>
                        <Col sm={12} className="siChartBox" >
                            {(hasData !== undefined && hasData) &&
                                <Plot divId="emissionsChart" className='emissionsChart' key='emissionsChart' data={chartModel.dataEmissions} layout={chartModel.layoutEmissions} config={chartModel.config} useResizeHandler={true} style={{ width: "100%" }} onHover={handleOnHoverEmissions} />
                            }
                            {(hasData === undefined || !hasData) &&
                                <div className="heading5">No Data Available</div>
                            }
                        </Col>
                    </Row>
                }
            </Col>
        </Row>
    );
}
